const SPKI_HEADER = '-----BEGIN PUBLIC KEY-----';
const SPKI_FOOTER = '-----END PUBLIC KEY-----';

const strictBase64Pattern = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

const pemBodyToBytes = (pemBody: string): Uint8Array | null => {
  const normalizedBody = pemBody.replace(/\s+/g, '');
  if (!normalizedBody || !strictBase64Pattern.test(normalizedBody)) {
    return null;
  }

  try {
    const binary = atob(normalizedBody);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
};

const importableRsaPublicKey = async (spkiBytes: Uint8Array): Promise<boolean> => {
  if (!globalThis.crypto?.subtle) {
    return false;
  }

  try {
    await globalThis.crypto.subtle.importKey(
      'spki',
      spkiBytes as BufferSource,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );
    return true;
  } catch {
    return false;
  }
};

export const getPemPublicKeyError = async (content: string): Promise<string | null> => {
  const trimmed = content.trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.includes('PRIVATE KEY')) {
    return 'integrations.security.private_key_not_allowed';
  }

  if (!trimmed.startsWith(SPKI_HEADER) || !trimmed.endsWith(SPKI_FOOTER)) {
    return 'integrations.security.invalid_pem_format';
  }

  const pemBody = trimmed.slice(SPKI_HEADER.length, -SPKI_FOOTER.length).trim();

  const spkiBytes = pemBodyToBytes(pemBody);
  if (!spkiBytes) {
    return 'integrations.security.invalid_public_key';
  }

  const isValid = await importableRsaPublicKey(spkiBytes);

  return isValid ? null : 'integrations.security.invalid_public_key';
};
